import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import { getCurrentUser, AuthUser } from '@/api/user';

/**
 * Microsoft Permissions Card
 * Displays Microsoft Graph permissions and reconnection options
 */
export const MicrosoftPermissionsCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [data, setData] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        setData(user);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(true);
      // Both reconnect and refresh redirect to the OAuth connect flow
      window.location.href = '/auth/connect';
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading permissions...</div>;
  }

  if (!data) {
    return <div style={{ padding: '1rem', color: '#666' }}>No permission data available</div>;
  }

  const connected = data.scopesVerified || (data.grantedScopes && data.grantedScopes.length > 0);

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <strong>Status:</strong>{' '}
          <span style={{ color: connected ? '#28a745' : '#dc3545' }}>
            {connected ? 'Connected' : 'Not connected'}
          </span>
          {data.scopesVerified !== undefined && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#999' }}>
              {data.scopesVerified ? '(verified)' : '(unverified)'}
            </span>
          )}
        </div>

        {data.grantedScopes && data.grantedScopes.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <strong>Granted Scopes:</strong>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {data.grantedScopes.map((scope) => (
                <li key={scope} style={{ fontSize: '0.9rem' }}>
                  {scope}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress ? 'not-allowed' : 'pointer',
              opacity: actionInProgress ? 0.6 : 1,
            }}
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

MicrosoftPermissionsCard.displayName = 'MicrosoftPermissionsCard';

export default MicrosoftPermissionsCard;
